import { Button, EButtonColorVariant } from '@outdoorsyco/bonfire';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import FavoritesLight from '@/assets/favorites/Favs-Light.svg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import Loading from '@/components/switchback/Loading';
import Fade from '@/components/switchback/Transition/Fade';
import { ListingTile } from '@/components/ui/ListingTile';
import { PageEmptyState } from '@/components/ui/PageEmptyState';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { TRootState } from '@/redux/rootReducer';
import { getFlexibleDateRangeSelected } from '@/redux/selectors/queryParams';
import { getSavedRentalResults } from '@/redux/selectors/savedRentals';
import {
  trackListingImageViewedEvent,
  trackListingSelectedEvent,
} from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { trackEvent } from '@/services/track-event';
import { IRentalTile } from '@/utility/mapSearchResultToTile';
import { openSessionModal } from '@/utility/session';

interface ITilesGridProps {
  rentals: IRentalTile[];
  onClickRental: (rental: IRentalTile, index: number) => void;
  onChangeImage: (rental: IRentalTile, index: number, nextIndex: number) => void;
}

const TilesGrid: React.FC<ITilesGridProps> = ({ rentals = [], onClickRental, onChangeImage }) => {
  const { isAboveDesktop } = useBreakpoint();

  const superhostBadgeEnabled = useExperimentIsEnabled(OptimizelyFlags.SUPERHOST_BADGE);

  if (!rentals.length) return null;

  const onClick = (rental: IRentalTile | undefined, index: number) => {
    if (rental) {
      trackEvent({
        event: 'click/card/rental',
        source: 'saved',
        rental_id: rental.id,
      });
      onClickRental(rental, index);
    }
  };

  return (
    <ul className="grid grid-cols-1 xl:grid-cols-4 sm:grid-cols-2 gap-10 mb-11 lg:mb-12">
      {rentals.map((rental, idx) => (
        <li key={`saved-rental-${idx}`}>
          <Fade duration={150}>
            <ListingTile
              rentalTile={rental}
              target={isAboveDesktop ? '_blank' : undefined}
              onSlideChange={nextIndex => onChangeImage(rental, idx, nextIndex)}
              onClick={() => onClick(rental, idx)}
              showSuperhost={superhostBadgeEnabled}
              wishlistingEventData={{
                index: idx,
                eventSource: EListingSource.GRID_LAYOUT,
              }}
              dataTestId="saved-rentals"
            />
          </Fade>
        </li>
      ))}
    </ul>
  );
};

const SavedRentalsTemplate = () => {
  const intl = useIntl();
  const isAuthenticated = useSelector(selectAuthenticated);

  const flexibleDateRangeSelected = useSelector(getFlexibleDateRangeSelected);
  const { isFetched: loadedSavedRentals, data: savedRentals } = useSelector((state: TRootState) =>
    getSavedRentalResults(state, null),
  );

  const onClickRental = (rental: IRentalTile, index: number) => {
    if (rental?.id) {
      trackListingSelectedEvent({
        rental,
        eventSource: EListingSource.GRID_LAYOUT,
        listingPlacement: index + 1,
        flexibleDateRange: flexibleDateRangeSelected,
      });
    }
  };

  const onChangeImage = (rental: IRentalTile, index: number, nextIndex: number) => {
    if (rental?.id) {
      trackListingImageViewedEvent(rental, 'saved', index + 1, nextIndex + 1);
    }
  };

  if (!isAuthenticated) {
    return (
      <section>
        <MaxWidth className="mx-auto my-8">
          <PageEmptyState
            icon={<FavoritesLight className="h-auto w-[180px] lg:w-auto" />}
            title={<FormattedMessage defaultMessage="No favorites yet" id="avWTtY" />}
            description={
              <FormattedMessage
                defaultMessage="But, it’s easy to fix. Just start with an account or by logging in."
                id="voCgOs"
              />
            }
            actions={
              <div className="flex flex-col w-full mt-8 md:flex-row md:w-auto">
                <Button
                  variant={EButtonColorVariant.Primary}
                  label={intl.formatMessage({ defaultMessage: 'Create an account', id: '0vL5u1' })}
                  onClick={() => openSessionModal('signup')}
                />

                <Button
                  className="mt-4 md:mt-0 md:ml-4"
                  variant={EButtonColorVariant.Secondary}
                  label={intl.formatMessage({ defaultMessage: 'Log in', id: 'odXlk8' })}
                  onClick={() => openSessionModal('login')}
                />
              </div>
            }
          />
        </MaxWidth>
      </section>
    );
  }

  if (!loadedSavedRentals) {
    return <Loading />;
  }

  return (
    <section>
      <MaxWidth className="mx-auto my-8">
        {!savedRentals.length ? (
          <PageEmptyState
            icon={<FavoritesLight className="h-auto w-[180px] lg:w-auto" />}
            title={<FormattedMessage defaultMessage="No favorites yet" id="avWTtY" />}
            description={
              <FormattedMessage
                defaultMessage="But, it’s easy to fix. Just tap a listing’s heart icon to save it to your favorites."
                id="Dc7MhU"
              />
            }
          />
        ) : (
          <>
            <h2 className="mb-8 text-gray-800 h2">
              <FormattedMessage defaultMessage="Favorites" id="SMrXWc" />
            </h2>

            <TilesGrid
              rentals={savedRentals}
              onClickRental={onClickRental}
              onChangeImage={onChangeImage}
            />
          </>
        )}
      </MaxWidth>
    </section>
  );
};

export default memo(SavedRentalsTemplate);
